import React from "react";
import { useHistory } from "react-router-dom";
import IconButton from "../ui/IconButton";
import { useSidebar } from "../../layouts/RCAdmin/SidebarContext";
import NotificationsBell from "./NotificationsBell";
import ThemeSwitch from "./ThemeSwitch";
import "../ui/rc-ui.scss";

const IBack = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M7 3L2 8L7 13M2 8H14"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IMenu = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 7h16M4 12h16M4 17h16"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const IUser = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle
      cx="12"
      cy="7"
      r="4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IBell = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.73 21a2 2 0 0 1-3.46 0"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Shared page title topbar.
 *
 * On mobile the sticky nav row lives in AppShell `MobileTopbar`; this header's
 * title and tabs scroll away. Duplicate nav icons in the nav strip are hidden
 * via CSS (see rc-ui.scss).
 *
 * There is no `flush` variant: the row renders the same in every shell, and
 * `rc-ui.scss` adapts it to a padded `.rc-page` parent on its own.
 *
 * @example
 * <Topbar
 *   title="Manage Listings"
 *   description="View, edit, and organize your listings"
 *   showBackButton
 *   backTo="/requests/orders"
 * />
 */
const Topbar = ({
  title,
  description,
  showBackButton = true,
  onBack,
  backTo,
  actions,
  className = "",
  children,
}) => {
  const history = useHistory();
  const { openMobileNav } = useSidebar();

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (backTo) {
      history.push(backTo);
      return;
    }
    history.goBack();
  };

  const goToProfile = () => history.push("/profile");


  return (
    <header className={["rc-ui-topbar", className].filter(Boolean).join(" ")}>
      <div className="rc-ui-topbar__heading">
        {showBackButton && (
          <IconButton
            variant="back"
            size="md"
            aria-label="Back"
            onClick={handleBack}
            // eslint-disable-next-line no-restricted-syntax -- TODO(M2x): already <IconButton>; .rc-back-btn only carries the legacy 36px square from rc-admin.scss. Phase 3 drops it.
            className="rc-back-btn"
          >
            <IBack />
          </IconButton>
        )}
        <div className="rc-ui-topbar__titles">
          {title != null && <h1 className="rc-ui-topbar__title">{title}</h1>}
          {description ? (
            <p className="rc-ui-topbar__description">{description}</p>
          ) : null}
          {children}
        </div>
      </div>

      <div className="rc-ui-topbar__nav-strip">
        <button
          type="button"
          // eslint-disable-next-line no-restricted-syntax -- TODO(M2x): legacy control. The class drives the mobile-only display (rc-admin.scss); Phase 3 migrates it to <IconButton>.
          className="rc-menu-btn rc-topbar-control--mobile"
          onClick={openMobileNav}
          aria-label="Open menu"
        >
          <IMenu />
        </button>

        <div className="rc-ui-topbar__right">
          {actions}
          <ThemeSwitch />
          <button
            type="button"
            // eslint-disable-next-line no-restricted-syntax -- TODO(M2x): legacy control. The class drives the rc-ui.scss :has() nav-strip rules; Phase 3 migrates it to <IconButton>.
            className="rc-ui-topbar__icon-btn"
            aria-label="Profile"
            onClick={goToProfile}
          >
            <IUser />
          </button>
          <NotificationsBell IconComponent={IBell} />
        </div>
      </div>
    </header>
  );
};

export default Topbar;
